import { defineComponent } from "vue";
import { VueEmit, VueProp } from "../../core/vueHelpers";
import { Classes } from "../classes";
import { CachedPortalData } from "../mainMenuCache";
import { ModuleButtonData, ModuleMenuScreenCategoryData, ModuleMenuScreenData, PortalButtonData } from "../menuData";
import CategoryList from "./categoryList";
import { FocusHandler } from "./focusHandler";
import ModuleList from "./moduleList";
import PortalBar from "./portalBar";
import { ScreenButtonClasses } from "./screenButton";

const centerPanelScreenButtonClasses: ScreenButtonClasses =
{
    unselectedClass: Classes.centerPanelScreen,
    selectedClass: Classes.centerPanelScreenSelected,
    labelClass: Classes.centerPanelScreenText,
    newWindowIconClass: "menuCenterPanel"
};
const rightPanelScreenButtonClasses: ScreenButtonClasses =
{
    unselectedClass: Classes.rightPanelScreen,
    selectedClass: Classes.rightPanelScreenSelected,
    labelClass: Classes.rightPanelScreenText,
    newWindowIconClass: "menuRightPanel"
};

export default /*#__PURE__*/ defineComponent(
    {
        name: "desktopMainMenu",
        props:
        {
            selectedPortalCode: VueProp(String),
            portalButtonDatas: VueProp<PortalButtonData[]>(Array),
            selectedPortal: VueProp<CachedPortalData>(),
            moduleMenuScreenCategoryDatas: VueProp<ModuleMenuScreenCategoryData[]>(Array),
            focusHandler: VueProp<FocusHandler>()
        },
        emits:
        {
            selectPortal: VueEmit<(portalButtonData: PortalButtonData) => boolean>(),
            selectModule: VueEmit<(moduleButtonData: ModuleButtonData) => boolean>(),
            selectScreen: VueEmit<(portalButtonData: ModuleMenuScreenData, newWindow: boolean, element: HTMLElement) => boolean>(),
        },
        render()
        {
            const centerPanelData: ModuleMenuScreenCategoryData[] = [];
            const rightPanelData: ModuleMenuScreenCategoryData[] = [];

            this.moduleMenuScreenCategoryDatas.forEach(function (moduleMenuScreenCategoryData: ModuleMenuScreenCategoryData)
            {
                if (moduleMenuScreenCategoryData.displayOrder === null || moduleMenuScreenCategoryData.displayOrder <= 2)
                {
                    centerPanelData.push(moduleMenuScreenCategoryData);
                }
                else
                {
                    rightPanelData.push(moduleMenuScreenCategoryData);
                }
            });

            const portalBar = (
                <PortalBar
                    selectedPortalCode={this.selectedPortalCode}
                    portalButtonDatas={this.portalButtonDatas}
                    focusHandler={this.focusHandler}
                    onSelectPortal=
                    {
                        (portalButtonData: PortalButtonData) =>
                        {
                            (this.$refs.modules as HTMLElement).scrollTop = 0;
                            (this.$refs.centerPanel as HTMLElement).scrollTop = 0;
                            (this.$refs.rightPanel as HTMLElement).scrollTop = 0;
                            this.$emit("selectPortal", portalButtonData);
                        }
                    }
                />
            );

            const moduleList = (
                <ModuleList
                    selectedModuleID={this.selectedPortal.selectedModuleID!}
                    moduleButtonDatas={this.selectedPortal.moduleButtons}
                    focusHandler={this.focusHandler}
                    onSelectModule=
                    {
                        (moduleButtonData: ModuleButtonData) =>
                        {
                            (this.$refs.centerPanel as HTMLElement).scrollTop = 0;
                            (this.$refs.rightPanel as HTMLElement).scrollTop = 0;
                            this.$emit("selectModule", moduleButtonData);
                        }
                    }
                />
            );

            const centerPanelCategories = centerPanelData.map((data) =>
                <CategoryList
                    class={Classes.centerPanelCategory}
                    moduleMenuScreenCategoryData={data}
                    labelClass={Classes.centerPanelCategoryText}
                    screenButtonClasses={centerPanelScreenButtonClasses}
                    focusHandler={this.focusHandler}
                    onSelectScreen={(moduleMenuScreenData: ModuleMenuScreenData, newWindow: boolean, element: HTMLElement) => this.$emit("selectScreen", moduleMenuScreenData, newWindow, element)}
                />
            );

            const rightPanelCategories = rightPanelData.map((data) =>
                <CategoryList
                    class={Classes.rightPanelCategory}
                    moduleMenuScreenCategoryData={data}
                    labelClass={Classes.rightPanelCategoryText}
                    screenButtonClasses={rightPanelScreenButtonClasses}
                    focusHandler={this.focusHandler}
                    onSelectScreen={(moduleMenuScreenData: ModuleMenuScreenData, newWindow: boolean, element: HTMLElement) => this.$emit("selectScreen", moduleMenuScreenData, newWindow, element)}
                />
            );

            return (
                <nav class={Classes.menu} aria-label="Main Menu">
                    {/* Portal Bar */}
                    {portalBar}
                    {/* Portal Panel */}
                    <div id={Classes.portalContent} class={Classes.portalContent} role="tabpanel">
                        {/* Left Panel */}
                        <div class={Classes.moduleContainer} role="region" aria-label="Modules" ref="modules">
                            {moduleList}
                        </div>
                        {/* Module Screens Container */}
                        <div class={Classes.moduleScreensContainer} role="region" aria-label="Module Links">
                            <div id={Classes.moduleScreens} class={Classes.moduleScreens} role="menubar">
                                {/* Center Panel */}
                                <div class={Classes.centerPanel} ref="centerPanel">
                                    {centerPanelCategories}
                                </div>
                                {/* Right Panel */}
                                <div class={Classes.rightPanel} ref="rightPanel">
                                    {rightPanelCategories}
                                </div>
                            </div>
                        </div>
                    </div>
                </nav>
            );
        }
    });